import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  if (!base64String || typeof base64String !== 'string') {
    return new Uint8Array();
  }
  try {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  } catch {
    return new Uint8Array();
  }
}

const NotificationContext = createContext(null);

// ── Notification chime (Web Audio API — no external file needed) ──────────
function playNotificationSound() {
  if (typeof window === 'undefined') return;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return;

  try {
    const ctx = new AudioContextClass();
    const gain = ctx.createGain();
    gain.connect(ctx.destination);

    [{ freq: 880, delay: 0 }, { freq: 1100, delay: 0.18 }].forEach(({ freq, delay }) => {
      const osc = ctx.createOscillator();
      osc.connect(gain);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + delay);
      gain.gain.setValueAtTime(0.35, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.4);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.42);
    });

    setTimeout(() => {
      try {
        if (ctx.state !== 'closed') ctx.close();
      } catch {}
    }, 1200);
  } catch {}
}

// ── Browser permission ────────────────────────────────────────────────────
async function requestPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    const r = await Notification.requestPermission();
    return r === 'granted';
  } catch {
    return false;
  }
}

const getStoredToken = () => {
  try {
    return localStorage.getItem('token') || null;
  } catch {
    return null;
  }
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isMounted = useRef(true);
  const permissionAsked = useRef(false);
  const loadedOnce = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ── Register Service Worker + Web Push subscription ───────────────────
  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (!getStoredToken() || !VAPID_PUBLIC_KEY) return;

    let isSubscribed = true;

    async function registerPush() {
      try {
        const granted = await requestPermission();
        if (!granted || !isSubscribed) return;

        const reg = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
        if (!isSubscribed) return;

        // Check if already subscribed
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          const appServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
          if (appServerKey.length === 0) return;

          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: appServerKey,
          });
        }
        if (isSubscribed && sub) {
          await api.post('/notifications/push-subscribe', sub.toJSON());
        }
      } catch (err) {
        console.warn('[PUSH] Registration failed:', err.message);
      }
    }

    registerPush();

    return () => {
      isSubscribed = false;
    };
  }, []);

  // ── Handle PUSH_CLICK from Service Worker (navigate to inbox) ─────────
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const handler = (e) => {
      if (e.data?.type === 'PUSH_CLICK' && e.data.phone) {
        navigate('/inbox', { state: { openPhone: e.data.phone } });
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  // ── Ask browser permission once ────────────────────────────────────────
  useEffect(() => {
    if (!permissionAsked.current) {
      permissionAsked.current = true;
      requestPermission();
    }
  }, []);

  // ── Load from DB on mount (once token is available) ───────────────────
  const loadFromDB = useCallback(async () => {
    if (!getStoredToken()) return;
    try {
      setLoading(true);
      const { data } = await api.get('/notifications');
      if (!isMounted.current) return;

      const notifList = Array.isArray(data) ? data : [];
      setNotifications(notifList);
      setUnreadCount(notifList.filter(n => !n.read).length);
      loadedOnce.current = true;
    } catch {
      // Ignore network errors on notification poll
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (getStoredToken()) {
      loadFromDB();
    }
  }, [loadFromDB]);

  // ── Called when socket notification:new or inbox:new fires ─────────
  const addNotification = useCallback(({ _id, title, body, phone, senderName, source, createdAt, read }) => {
    const isIg = source === 'instagram' || String(phone || '').startsWith('ig_');
    const notifId = _id ? String(_id) : null;
    const incomingTime = new Date(createdAt || Date.now()).getTime();

    setNotifications(prev => {
      // 1. Avoid duplicate by DB _id
      if (notifId && prev.some(n => String(n._id) === notifId)) return prev;

      // 2. Avoid duplicate by recent phone + body match (within 4 second window)
      const isRecentDuplicate = prev.some(n => {
        const itemTime = new Date(n.createdAt || Date.now()).getTime();
        const timeDiff = Math.abs(incomingTime - itemTime);
        return n.phone === phone && n.body === body && timeDiff < 4000;
      });
      if (isRecentDuplicate) return prev;

      // Increment unread count ONLY when a new non-duplicate notification is actually added
      if (!read) {
        setUnreadCount(c => c + 1);
      }

      const notif = {
        _id:        _id || (`temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`),
        title:      title || (isIg ? `📸 Instagram DM from ${senderName || 'User'}` : `💬 Message from ${senderName || phone}`),
        body:       body  || '',
        phone:      phone || '',
        senderName: senderName || '',
        source:     isIg ? 'instagram' : 'whatsapp',
        createdAt:  createdAt || new Date().toISOString(),
        read:       read || false,
      };

      return [notif, ...prev].slice(0, 50);
    });

    // Sound
    playNotificationSound();

    // Browser OS notification (only when tab is hidden)
    if (typeof document !== 'undefined' && document.hidden && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        const notifTitle = title || (isIg ? `📸 Instagram DM from ${senderName || 'User'}` : `💬 Message from ${senderName || phone}`);
        const bn = new Notification(notifTitle, {
          body:      body || '',
          icon:      '/logo.png',
          badge:     '/logo.png',
          tag:       isIg ? `ig-${phone}` : `inbox-${phone}`,
          renotify:  true,
        });

        bn.onclick = () => {
          window.focus();
          const nav = window.__reactRouterNavigate || navigate;
          if (typeof nav === 'function') {
            nav(isIg ? '/instagram' : '/inbox', { state: { openPhone: phone } });
          }
          bn.close();
        };

        setTimeout(() => {
          try { bn.close(); } catch {}
        }, 6000);
      } catch {}
    }
  }, [navigate]);

  // ── Sync unread count from server (called by socket notification:count) ─
  const syncUnreadCount = useCallback((count) => {
    setUnreadCount(Number(count) || 0);
  }, []);

  // ── Mark single notification as read ──────────────────────────────────
  const markOneRead = useCallback(async (id) => {
    setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await api.put(`/notifications/${id}/read`);
    } catch {}
  }, []);

  // ── Mark all as read ───────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await api.put('/notifications/mark-all-read');
    } catch {}
  }, []);

  // ── Delete single notification ─────────────────────────────────────────
  const deleteOne = useCallback(async (id) => {
    setNotifications(prev => {
      const n = prev.find(x => x._id === id);
      if (n && !n.read) setUnreadCount(c => Math.max(0, c - 1));
      return prev.filter(x => x._id !== id);
    });
    try {
      await api.delete(`/notifications/${id}`);
    } catch {}
  }, []);

  // ── Clear all notifications ────────────────────────────────────────────
  const clearAll = useCallback(async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await api.delete('/notifications');
    } catch {}
  }, []);

  // ── Toggle panel open/close ────────────────────────────────────────────
  const toggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    isOpen,
    loading,
    addNotification,
    syncUnreadCount,
    markOneRead,
    markAllRead,
    deleteOne,
    clearAll,
    toggleOpen,
    close,
    loadFromDB,
  }), [
    notifications,
    unreadCount,
    isOpen,
    loading,
    addNotification,
    syncUnreadCount,
    markOneRead,
    markAllRead,
    deleteOne,
    clearAll,
    toggleOpen,
    close,
    loadFromDB,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;

